import { QueryClient, QueryClientProvider, matchQuery, MutationCache, type QueryKey } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { AuthProvider } from "@/hooks/auth/useAuth";
import { getTokenFromLocalStorage, type AuthContextType } from "@/hooks/auth/auth";
import { useAuth } from "@/hooks/auth/auth";
import { DarkModeProvider } from "@/hooks/dark-mode/useDarkMode";
import { RootPendingComponent } from "@/routes/-components/root-pending-component";
import { routeTree } from "./routeTree.gen";

export const rootPendingMs = 150;
export const rootPendingMinMs = 300;

export const rootPendingRouterOptions = {
  defaultPendingComponent: RootPendingComponent,
  defaultPendingMs: rootPendingMs,
  defaultPendingMinMs: rootPendingMinMs,
} as const;

const createAppQueryClient = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60,
      },
    },
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        const invalidates = mutation.meta?.invalidates;

        if (invalidates === "none") {
          return;
        }

        if (Array.isArray(invalidates)) {
          queryClient.invalidateQueries({
            predicate: (query) => (invalidates as QueryKey[]).some((queryKey) => matchQuery({ queryKey }, query)),
          });

          return;
        }

        queryClient.invalidateQueries();
      },
    }),
  });

  return queryClient;
};

const createAuthBridge = () => {
  let setToken: AuthContextType["setToken"] = () => undefined;
  let logout: AuthContextType["logout"] = () => undefined;

  return {
    auth: {
      get isAuthenticated() {
        return Boolean(getTokenFromLocalStorage());
      },
      get token() {
        return getTokenFromLocalStorage();
      },
      setToken(value) {
        setToken(value);
      },
      logout() {
        logout();
      },
    } as AuthContextType,
    update(nextAuth: AuthContextType) {
      setToken = nextAuth.setToken;
      logout = nextAuth.logout;
    },
  };
};

export function getRouter() {
  const queryClient = createAppQueryClient();
  const authBridge = createAuthBridge();

  const AuthRouterSync = ({ children }: { children: React.ReactNode }) => {
    const auth = useAuth();

    useEffect(() => {
      authBridge.update(auth);
      void router.invalidate();
    }, [auth]);

    return children;
  };

  const router = createRouter({
    routeTree,
    context: {
      auth: authBridge.auth,
      queryClient,
    },
    defaultPreload: "intent",
    scrollRestoration: true,
    defaultStructuralSharing: true,
    defaultPreloadStaleTime: 0,
    ...rootPendingRouterOptions,
    Wrap: ({ children }) => (
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <DarkModeProvider>
            <AuthRouterSync>{children}</AuthRouterSync>
          </DarkModeProvider>
        </AuthProvider>
      </QueryClientProvider>
    ),
  });

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
